import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faCog, 
  faSpinner,
  faTimes,
  faCheck,
  faToggleOn,
  faToggleOff,
  faPlus,
  faTrash,
  faExclamationTriangle,
  faShieldAlt,
  faList
} from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './FeatureManagementPage.css';

const FeatureManagementPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  // State management
  const [allFeatures, setAllFeatures] = useState([]);
  const [rolesWithFeatures, setRolesWithFeatures] = useState([]);
  const [roleMappings, setRoleMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, roles, mappings
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [assignForm, setAssignForm] = useState({
    role: '',
    featureCode: '',
    enabled: true,
    notes: ''
  });
  
  const [removeForm, setRemoveForm] = useState({
    role: '',
    featureCode: ''
  });
  
  const [statusForm, setStatusForm] = useState({
    featureCode: '',
    enabled: true,
    reason: ''
  });

  // Fetch all data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchAllFeatures(),
        fetchRolesWithFeatures()
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // API Call: Get all features
  const fetchAllFeatures = async () => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/features`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch features. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        setAllFeatures(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to load features');
      }
    } catch (err) {
      console.error('Error fetching features:', err);
      throw err;
    }
  };

  // API Call: Get all roles with features
  const fetchRolesWithFeatures = async () => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/features/roles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch roles. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        setRolesWithFeatures(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to load roles');
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      throw err;
    }
  };

  // API Call: Get role mappings
  const fetchRoleMappings = async (role) => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/features/mappings/${role}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch mappings. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        setRoleMappings(prev => ({
          ...prev,
          [role]: result.data || []
        }));
      } else {
        throw new Error(result.message || 'Failed to load mappings');
      }
    } catch (err) {
      console.error('Error fetching role mappings:', err);
      throw err;
    }
  };

  // API Call: Assign feature to role
  const assignFeatureToRole = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/features/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assignForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to assign feature. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        setShowAssignModal(false);
        setAssignForm({ role: '', featureCode: '', enabled: true, notes: '' });
        await loadAllData();
        alert('Feature assigned successfully!');
      } else {
        throw new Error(result.message || 'Failed to assign feature');
      }
    } catch (err) {
      console.error('Error assigning feature:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // API Call: Remove feature from role
  const removeFeatureFromRole = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/features/remove`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(removeForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to remove feature. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        setShowRemoveModal(false);
        setRemoveForm({ role: '', featureCode: '' });
        await loadAllData();
        alert('Feature removed successfully!');
      } else {
        throw new Error(result.message || 'Failed to remove feature');
      }
    } catch (err) {
      console.error('Error removing feature:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // API Call: Update feature status
  const updateFeatureStatus = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/features/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update status. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        setShowStatusModal(false);
        setStatusForm({ featureCode: '', enabled: true, reason: '' });
        await loadAllData();
        alert('Feature status updated successfully!');
      } else {
        throw new Error(result.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating feature status:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const openAssignModal = (role = '', featureCode = '') => {
    setAssignForm({ role, featureCode, enabled: true, notes: '' });
    setShowAssignModal(true);
  };

  const openRemoveModal = (role = '', featureCode = '') => {
    setRemoveForm({ role, featureCode });
    setShowRemoveModal(true);
  };

  const openStatusModal = (feature = null) => {
    if (feature) {
      setStatusForm({ 
        featureCode: feature.featureCode, 
        enabled: !feature.enabled, 
        reason: '' 
      });
    } else {
      setStatusForm({ featureCode: '', enabled: true, reason: '' });
    }
    setShowStatusModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPERADMIN':
        return '#e91e63';
      case 'ADMIN':
        return '#2196f3';
      case 'TICKET_VOLUNTEER':
        return '#4caf50';
      case 'SALES_VOLUNTEER':
        return '#ff9800';
      default:
        return '#666';
    }
  };

  const handleViewMappings = async (role) => {
    if (!roleMappings[role]) {
      try {
        await fetchRoleMappings(role);
      } catch (err) {
        setError(err.message);
      }
    }
    setActiveTab('mappings');
  };

  return (
    <div className="feature-management-container">
      <div className="feature-management-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>
        
        <div className="header-content">
          <h2><FontAwesomeIcon icon={faCog} /> Feature Management</h2>
          <p>Manage system features and role permissions</p>
        </div>
        
        <div className="header-actions">
          <button 
            className="action-button primary-button"
            onClick={() => openAssignModal()}
          >
            <FontAwesomeIcon icon={faPlus} /> Assign Feature
          </button>
          <button 
            className="action-button secondary-button"
            onClick={() => openRemoveModal()}
          >
            <FontAwesomeIcon icon={faTrash} /> Remove Feature
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FontAwesomeIcon icon={faList} /> All Features
        </button>
        <button 
          className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          <FontAwesomeIcon icon={faShieldAlt} /> Roles & Features
        </button>
      </div>

      {loading ? (
        <div className="loading-indicator">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Loading features...</span>
        </div>
      ) : (
        <>
          {/* All Features Tab */}
          {activeTab === 'overview' && (
            <div className="features-overview">
              <div className="section-header">
                <h3>All Available Features</h3>
                <p>Manage global feature availability</p>
              </div>
              
              <div className="features-table-container">
                <table className="features-table">
                  <thead>
                    <tr>
                      <th>Feature Code</th>
                      <th>Display Name</th>
                      <th>Description</th>
                      <th>Category</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allFeatures.length > 0 ? (
                      allFeatures.map((feature) => (
                        <tr key={feature.id}>
                          <td><code>{feature.featureCode}</code></td>
                          <td><strong>{feature.displayName}</strong></td>
                          <td className="description-cell">{feature.description}</td>
                          <td><span className="category-badge">{feature.category}</span></td>
                          <td className="text-center">{feature.displayOrder}</td>
                          <td>
                            <span className={`status-badge ${feature.enabled ? 'enabled' : 'disabled'}`}>
                              <FontAwesomeIcon icon={feature.enabled ? faCheck : faTimes} />
                              {feature.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td>
                            <button
                              className={`action-button-small ${feature.enabled ? 'warning' : 'success'}`}
                              onClick={() => openStatusModal(feature)}
                              title={`${feature.enabled ? 'Disable' : 'Enable'} Feature`}
                            >
                              <FontAwesomeIcon icon={feature.enabled ? faToggleOff : faToggleOn} />
                              {feature.enabled ? 'Disable' : 'Enable'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="no-records">
                          No features found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Roles & Features Tab */}
          {activeTab === 'roles' && (
            <div className="roles-features">
              <div className="section-header">
                <h3>Roles & Assigned Features</h3>
                <p>View and manage feature assignments for each role</p>
              </div>
              
              <div className="roles-grid">
                {rolesWithFeatures.map((roleData) => (
                  <div key={roleData.role} className="role-card">
                    <div className="role-card-header" style={{ borderLeftColor: getRoleColor(roleData.role) }}>
                      <div className="role-info">
                        <h4 style={{ color: getRoleColor(roleData.role) }}>
                          <FontAwesomeIcon icon={faShieldAlt} /> {roleData.displayName}
                        </h4>
                        <p>{roleData.description}</p>
                      </div>
                      <div className="feature-count">
                        <span className="count-number">{roleData.featureCount}</span>
                        <span className="count-label">Features</span>
                      </div>
                    </div>
                    
                    <div className="role-card-body">
                      <div className="features-list">
                        {roleData.features.map((featureCode) => {
                          const feature = allFeatures.find(f => f.featureCode === featureCode);
                          return (
                            <div key={featureCode} className="feature-item">
                              <div className="feature-item-info">
                                <FontAwesomeIcon icon={faCheck} className="feature-check" />
                                <span className="feature-code">{featureCode}</span>
                                {feature && <span className="feature-name">{feature.displayName}</span>}
                              </div>
                              {roleData.role !== 'SUPERADMIN' && (
                                <button
                                  className="remove-feature-btn"
                                  onClick={() => openRemoveModal(roleData.role, featureCode)}
                                  title="Remove this feature"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {roleData.role !== 'SUPERADMIN' && (
                        <div className="role-card-actions">
                          <button
                            className="action-button-small primary"
                            onClick={() => openAssignModal(roleData.role)}
                          >
                            <FontAwesomeIcon icon={faPlus} /> Add Feature
                          </button>
                        </div>
                      )}
                      
                      {roleData.role === 'SUPERADMIN' && (
                        <div className="superadmin-notice">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          SUPERADMIN permissions cannot be modified
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mappings Tab */}
          {activeTab === 'mappings' && (
            <div className="mappings-view">
              <div className="section-header">
                <h3>Role-Feature Mappings with Audit Trail</h3>
                <button 
                  className="action-button secondary-button"
                  onClick={() => setActiveTab('roles')}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Roles
                </button>
              </div>
              
              {Object.keys(roleMappings).map(role => (
                <div key={role} className="mappings-section">
                  <h4 style={{ color: getRoleColor(role) }}>{role}</h4>
                  <div className="mappings-table-container">
                    <table className="mappings-table">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th>Status</th>
                          <th>Granted By</th>
                          <th>Created At</th>
                          <th>Last Modified</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roleMappings[role]?.map((mapping) => (
                          <tr key={mapping.id}>
                            <td>
                              <div>
                                <code>{mapping.featureCode}</code>
                                <div className="feature-display-name">{mapping.featureDisplayName}</div>
                              </div>
                            </td>
                            <td>
                              <span className={`status-badge ${mapping.enabled ? 'enabled' : 'disabled'}`}>
                                {mapping.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td>{mapping.grantedBy || 'N/A'}</td>
                            <td>{formatDate(mapping.createdAt)}</td>
                            <td>
                              {mapping.lastModifiedAt ? (
                                <div>
                                  <div>{formatDate(mapping.lastModifiedAt)}</div>
                                  <div className="modified-by">by {mapping.lastModifiedBy}</div>
                                </div>
                              ) : (
                                'Never'
                              )}
                            </td>
                            <td className="notes-cell">{mapping.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Assign Feature Modal */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign Feature to Role</h3>
              <button 
                className="close-button"
                onClick={() => setShowAssignModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="assignRole">Role *</label>
                <select
                  id="assignRole"
                  value={assignForm.role}
                  onChange={(e) => setAssignForm({...assignForm, role: e.target.value})}
                  required
                >
                  <option value="">Select Role</option>
                  {rolesWithFeatures
                    .filter(r => r.role !== 'SUPERADMIN')
                    .map(r => (
                      <option key={r.role} value={r.role}>{r.displayName}</option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="assignFeature">Feature *</label>
                <select
                  id="assignFeature"
                  value={assignForm.featureCode}
                  onChange={(e) => setAssignForm({...assignForm, featureCode: e.target.value})}
                  required
                >
                  <option value="">Select Feature</option>
                  {allFeatures.map(f => (
                    <option key={f.featureCode} value={f.featureCode}>
                      {f.displayName} ({f.featureCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="assignEnabled">Status</label>
                <select
                  id="assignEnabled"
                  value={assignForm.enabled}
                  onChange={(e) => setAssignForm({...assignForm, enabled: e.target.value === 'true'})}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="assignNotes">Notes</label>
                <textarea
                  id="assignNotes"
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
                  placeholder="Add any notes about this assignment..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submit-button"
                onClick={assignFeatureToRole}
                disabled={submitting || !assignForm.role || !assignForm.featureCode}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Assigning...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    Assign Feature
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Feature Modal */}
      {showRemoveModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                Remove Feature from Role
              </h3>
              <button 
                className="close-button"
                onClick={() => setShowRemoveModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="warning-notice">
                <p>This will remove the feature access from the selected role.</p>
              </div>

              <div className="form-group">
                <label htmlFor="removeRole">Role *</label>
                <select
                  id="removeRole"
                  value={removeForm.role}
                  onChange={(e) => setRemoveForm({...removeForm, role: e.target.value})}
                  required
                >
                  <option value="">Select Role</option>
                  {rolesWithFeatures
                    .filter(r => r.role !== 'SUPERADMIN')
                    .map(r => (
                      <option key={r.role} value={r.role}>{r.displayName}</option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="removeFeature">Feature *</label>
                <select
                  id="removeFeature"
                  value={removeForm.featureCode}
                  onChange={(e) => setRemoveForm({...removeForm, featureCode: e.target.value})}
                  required
                  disabled={!removeForm.role}
                >
                  <option value="">Select Feature</option>
                  {removeForm.role && rolesWithFeatures
                    .find(r => r.role === removeForm.role)
                    ?.features.map(featureCode => {
                      const feature = allFeatures.find(f => f.featureCode === featureCode);
                      return (
                        <option key={featureCode} value={featureCode}>
                          {feature?.displayName || featureCode} ({featureCode})
                        </option>
                      );
                    })}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setShowRemoveModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-button-confirm"
                onClick={removeFeatureFromRole}
                disabled={submitting || !removeForm.role || !removeForm.featureCode}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Removing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} />
                    Remove Feature
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Feature Status Modal */}
      {showStatusModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Update Global Feature Status</h3>
              <button 
                className="close-button"
                onClick={() => setShowStatusModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="info-notice">
                <p>When a feature is globally disabled, no role can access it, even if assigned.</p>
              </div>

              <div className="form-group">
                <label htmlFor="statusFeature">Feature *</label>
                <select
                  id="statusFeature"
                  value={statusForm.featureCode}
                  onChange={(e) => setStatusForm({...statusForm, featureCode: e.target.value})}
                  required
                >
                  <option value="">Select Feature</option>
                  {allFeatures.map(f => (
                    <option key={f.featureCode} value={f.featureCode}>
                      {f.displayName} ({f.featureCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="statusEnabled">New Status *</label>
                <select
                  id="statusEnabled"
                  value={statusForm.enabled}
                  onChange={(e) => setStatusForm({...statusForm, enabled: e.target.value === 'true'})}
                >
                  <option value="true">Enable</option>
                  <option value="false">Disable</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="statusReason">Reason *</label>
                <textarea
                  id="statusReason"
                  value={statusForm.reason}
                  onChange={(e) => setStatusForm({...statusForm, reason: e.target.value})}
                  placeholder="Enter reason for status change..."
                  rows="3"
                  required
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setShowStatusModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submit-button"
                onClick={updateFeatureStatus}
                disabled={submitting || !statusForm.featureCode || !statusForm.reason}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Updating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    Update Status
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureManagementPage;

